
import React, { lazy, Suspense, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import TableSkeleton from '../../../components/TableSkeleton';
import FiltersSkeleton from '../../../components/FiltersSkeleton';
import { useViewFullData, ViewFullDataProvider } from '../../../contexts/extractions/ViewFullDataContext';
import { ExtractionsProvider } from '../../../contexts/extractions/ExtractionContext';
import { ExtCustomColumnsProvider } from '../../../contexts/extractions/ExtCustomColumnContext';
import { ExtCustomColumnQueriesProvider } from '../../../contexts/extractions/ExtCustomColumnQueryContext';
import ExtractionLayout from '../ExtractionLayout';
import { EyeIcon } from '@heroicons/react/24/solid';
import LabelTooltip from '../../../components/LabelTooltip';
import constants from '../../../commonUtils/constants';
import Spinner from '../../../components/Spinner';

const FullDataFilters = lazy(() => import('./FullDataFilters'));
const TanstackTable = lazy(() => import('../../../components/TanstackTable'));

/* -------------------- View Content -------------------- */

const ViewFullDataContent = () => {
    const {
        table,
        columns,
        fullData,
        pagination,
        handlePageChange,
    } = useViewFullData();
    
    const { FullDataList, fullDataLoading } = useSelector((state) => state.Extractions);
    const { extOutputMapping } = useSelector((state) => state.ExtOutputMappings);

    // Pagination values are ready once loading is done — even if they are 0
    const paginationReady = !fullDataLoading && FullDataList?.pagination != null;

    return (
        <>
            <div>
                <div className="flex justify-between items-center">
                    {/* Data Group and Category Info */}
                    <div className="flex gap-4 mb-4">
                        <div className="bg-accent px-4 py-2 rounded-md flex items-center gap-2">
                            <span className="text-sm font-medium">Data Group:</span>
                            {fullDataLoading ? (
                                <Spinner />
                            ) : (
                                <span className="text-sm">{extOutputMapping?.dataGroup?.title || 'N/A'}</span>
                            )}
                            <LabelTooltip
                                Icon={EyeIcon}
                                variant="sm"
                                content={
                                    <span className="ml-auto max-w-[400px]">
                                        {extOutputMapping?.dataGroup?.query || "Loading..."}
                                    </span>
                                }
                            />
                        </div>
                        <div className="bg-accent px-4 py-2 rounded-md flex items-center gap-2">
                            <span className="text-sm font-medium">Data Category:</span>
                            {fullDataLoading ? (
                                <Spinner/>
                            ) : (
                                <span className="text-sm">{extOutputMapping?.dataCategory?.title || 'N/A'}</span>
                            )}
                            <LabelTooltip
                                Icon={EyeIcon}
                                variant="sm"
                                content={
                                    <span className="ml-auto max-w-[400px]">
                                        {extOutputMapping?.dataCategory?.query || "Loading..."}
                                    </span>
                                }
                            />
                        </div>
                    </div>

                    {/* Total Pages and total columns */}
                    <div className="flex gap-4 mb-4">
                        <div className="bg-accent px-4 py-2 rounded-md flex items-center gap-2">
                            <span className="text-sm font-medium">Total Pages:</span>
                            {paginationReady ? (
                                <span className="text-sm">
                                    {FullDataList.pagination.totalPages ?? 0}
                                </span>
                            ) : (
                                <Spinner/>
                            )}
                        </div>
                        <div className="bg-accent px-4 py-2 rounded-md flex items-center gap-2">
                            <span className="text-sm font-medium">Total Records:</span>
                            {paginationReady ? (
                                <span className="text-sm">
                                    {FullDataList.pagination.total ?? 0}
                                </span>
                            ) : (
                                <Spinner/>
                            )}
                        </div>
                    </div>
                </div>
            
                <div className="flex justify-between mb-4">
                    <Suspense fallback={<FiltersSkeleton/>}>
                        <FullDataFilters />
                    </Suspense>
                </div>
            </div>
            
            <Suspense fallback={<TableSkeleton columns={10} rows={constants?.extraction?.fullDataListInitialState?.pagination?.perPage || 5} headers={["#", "Column 1", "Column 2", "Column 3", "Column 4"]}/>}> 
                <TanstackTable
                    loading={fullDataLoading}
                    table={table}
                    data={fullData || []}
                    columns={columns}
                    pagination={pagination}
                    handlePageChange={handlePageChange}
                    showEdgeNavigation={true}
                    className='bg-white'
                />
            </Suspense>
        </>
    );
};

/* -------------------- Page Wrapper -------------------- */


const ViewFullDataPageContent = () => {
    const { key, dataGroup, dataCategory } = useParams();
    const dispatch = useDispatch();

    useEffect(() => {
        // Cleanup: stop polling when unmounting
        return () => {
            dispatch({ type: 'STOP_POLL_INGESTION_PROGRESS' });
        };
    }, [dispatch]);

    return (
        <ExtractionLayout hideRightButton={true}>
            <ViewFullDataProvider 
                extVersionKey={key}
                dataGroup={dataGroup}
                dataCategory={dataCategory}
            >
                <ViewFullDataContent />
            </ViewFullDataProvider>
        </ExtractionLayout>
    );
};

/* -------------------- Providers -------------------- */

const ViewFullDataPage = () => (
    <ExtractionsProvider>
        <ExtCustomColumnsProvider>
            <ExtCustomColumnQueriesProvider>
                <ViewFullDataPageContent />
            </ExtCustomColumnQueriesProvider>
        </ExtCustomColumnsProvider>
    </ExtractionsProvider>
);

export default ViewFullDataPage;